import { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Select,
  FormControl,
  InputLabel,
  type SelectChangeEvent,
  Alert,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Search as SearchIcon,
  Add as AddIcon,
  PersonOff as PersonOffIcon,
  PersonAdd as PersonAddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { format } from 'date-fns';

// Type definitions for user management
interface User {
  id: string;
  email: string;
  name: string;
  role: 'athlete' | 'trainer' | 'both' | 'superuser';
  isActive: boolean;
  stravaConnected: boolean;
  createdAt: string;
  lastLoginAt: string | null;
}

interface UserFilters {
  role: string;
  status: string;
  stravaConnected: string;
}

interface AddUserFormData {
  email: string;
  name: string;
  role: 'athlete' | 'trainer' | 'both' | 'superuser';
}

// Validation schema for add user form
const addUserSchema = yup.object({
  email: yup.string().email('Invalid email address').required('Email is required'),
  name: yup.string().required('Name is required').min(2, 'Name must be at least 2 characters'),
  role: yup.string().oneOf(['athlete', 'trainer', 'both', 'superuser'], 'Invalid role').required('Role is required'),
});

const UsersManagementPage = () => {
  // State management
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<UserFilters>({
    role: 'all',
    status: 'all',
    stravaConnected: 'all',
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [addUserDialogOpen, setAddUserDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<'suspend' | 'activate' | 'delete' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Form handling for add user
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<AddUserFormData>({
    resolver: yupResolver(addUserSchema),
    defaultValues: {
      email: '',
      name: '',
      role: 'athlete',
    },
  });

  // Mock data - In production, this would come from RTK Query
  const mockUsers: User[] = useMemo(() => [
    {
      id: '1',
      email: 'athlete1@example.com',
      name: 'John Athlete',
      role: 'athlete',
      isActive: true,
      stravaConnected: true,
      createdAt: '2024-01-15T10:00:00Z',
      lastLoginAt: '2024-12-18T14:30:00Z',
    },
    {
      id: '2',
      email: 'trainer1@example.com',
      name: 'Sarah Trainer',
      role: 'trainer',
      isActive: true,
      stravaConnected: true,
      createdAt: '2024-02-20T09:00:00Z',
      lastLoginAt: '2024-12-19T08:15:00Z',
    },
    {
      id: '3',
      email: 'both1@example.com',
      name: 'Mike Both',
      role: 'both',
      isActive: false,
      stravaConnected: false,
      createdAt: '2024-03-10T11:00:00Z',
      lastLoginAt: '2024-11-30T16:45:00Z',
    },
  ], []);

  // Load users on mount
  useState(() => {
    setUsers(mockUsers);
  });

  // Filter and search logic
  const filteredUsers = useMemo(() => {
    return users.filter((user) => {
      const matchesSearch =
        searchQuery === '' ||
        user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.name.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesRole = filters.role === 'all' || user.role === filters.role;
      const matchesStatus = filters.status === 'all' || (filters.status === 'active' ? user.isActive : !user.isActive);
      const matchesStrava =
        filters.stravaConnected === 'all' ||
        (filters.stravaConnected === 'connected' ? user.stravaConnected : !user.stravaConnected);

      return matchesSearch && matchesRole && matchesStatus && matchesStrava;
    });
  }, [users, searchQuery, filters]);

  // Pagination
  const paginatedUsers = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredUsers.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredUsers, page, rowsPerPage]);

  // Handlers
  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setPage(0);
  }, []);

  const handleFilterChange = useCallback((filterName: keyof UserFilters) => (event: SelectChangeEvent) => {
    setFilters((prev) => ({ ...prev, [filterName]: event.target.value }));
    setPage(0);
  }, []);

  const handleChangePage = useCallback((event: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const handleMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>, user: User) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  }, []);

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleViewDetails = useCallback(() => {
    if (selectedUser) {
      console.log('View details for user:', selectedUser.id);
      setSuccess(`Viewing details for ${selectedUser.name}`);
    }
    handleMenuClose();
  }, [selectedUser, handleMenuClose]);

  const handleChangeRole = useCallback(() => {
    if (selectedUser) {
      console.log('Change role for user:', selectedUser.id);
      setSuccess(`Role change initiated for ${selectedUser.name}`);
    }
    handleMenuClose();
  }, [selectedUser, handleMenuClose]);

  const handleSuspendActivate = useCallback((action: 'suspend' | 'activate') => {
    setConfirmAction(action);
    setConfirmDialogOpen(true);
    handleMenuClose();
  }, [handleMenuClose]);

  const handleDelete = useCallback(() => {
    setConfirmAction('delete');
    setConfirmDialogOpen(true);
    handleMenuClose();
  }, [handleMenuClose]);

  const handleImpersonate = useCallback(() => {
    if (selectedUser) {
      console.log('Impersonate user:', selectedUser.id);
      setSuccess(`Impersonating ${selectedUser.name}`);
    }
    handleMenuClose();
  }, [selectedUser, handleMenuClose]);

  const handleConfirmAction = useCallback(() => {
    if (!selectedUser || !confirmAction) return;

    setLoading(true);
    setTimeout(() => {
      setUsers((prev) =>
        prev.map((user) => {
          if (user.id === selectedUser.id) {
            if (confirmAction === 'suspend') {
              return { ...user, isActive: false };
            } else if (confirmAction === 'activate') {
              return { ...user, isActive: true };
            } else if (confirmAction === 'delete') {
              return user;
            }
          }
          return user;
        })
      );

      if (confirmAction === 'delete') {
        setUsers((prev) => prev.filter((user) => user.id !== selectedUser.id));
      }

      setSuccess(
        `User ${selectedUser.name} ${
          confirmAction === 'delete' ? 'deleted' : confirmAction === 'suspend' ? 'suspended' : 'activated'
        } successfully`
      );
      setLoading(false);
      setConfirmDialogOpen(false);
      setConfirmAction(null);
      setSelectedUser(null);
    }, 1000);
  }, [selectedUser, confirmAction]);

  const handleAddUser = useCallback((data: AddUserFormData) => {
    setLoading(true);
    setTimeout(() => {
      const newUser: User = {
        id: String(users.length + 1),
        email: data.email,
        name: data.name,
        role: data.role,
        isActive: true,
        stravaConnected: false,
        createdAt: new Date().toISOString(),
        lastLoginAt: null,
      };
      setUsers((prev) => [...prev, newUser]);
      setSuccess(`User ${data.name} added successfully`);
      setAddUserDialogOpen(false);
      reset();
      setLoading(false);
    }, 1000);
  }, [users.length, reset]);

  const getRoleColor = (role: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (role) {
      case 'athlete':
        return 'primary';
      case 'trainer':
        return 'secondary';
      case 'both':
        return 'info';
      case 'superuser':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        User Management
      </Typography>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            placeholder="Search by email or name..."
            value={searchQuery}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
            sx={{ flexGrow: 1, minWidth: 250 }}
          />

          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Role</InputLabel>
            <Select value={filters.role} onChange={handleFilterChange('role')} label="Role">
              <MenuItem value="all">All Roles</MenuItem>
              <MenuItem value="athlete">Athlete</MenuItem>
              <MenuItem value="trainer">Trainer</MenuItem>
              <MenuItem value="both">Both</MenuItem>
              <MenuItem value="superuser">Superuser</MenuItem>
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select value={filters.status} onChange={handleFilterChange('status')} label="Status">
              <MenuItem value="all">All Status</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Strava</InputLabel>
            <Select value={filters.stravaConnected} onChange={handleFilterChange('stravaConnected')} label="Strava">
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="connected">Connected</MenuItem>
              <MenuItem value="disconnected">Disconnected</MenuItem>
            </Select>
          </FormControl>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddUserDialogOpen(true)}
            sx={{ ml: 'auto' }}
          >
            Add User
          </Button>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Strava</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Last Login</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && paginatedUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : paginatedUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  No users found
                </TableCell>
              </TableRow>
            ) : (
              paginatedUsers.map((user) => (
                <TableRow key={user.id} hover>
                  <TableCell>{user.name}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Chip label={user.role} color={getRoleColor(user.role)} size="small" />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={user.isActive ? 'Active' : 'Inactive'}
                      color={user.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={user.stravaConnected ? 'Connected' : 'Disconnected'}
                      color={user.stravaConnected ? 'success' : 'default'}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{format(new Date(user.createdAt), 'MMM dd, yyyy')}</TableCell>
                  <TableCell>
                    {user.lastLoginAt ? format(new Date(user.lastLoginAt), 'MMM dd, yyyy HH:mm') : 'Never'}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="More actions">
                      <IconButton onClick={(e) => handleMenuOpen(e, user)}>
                        <MoreVertIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredUsers.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleViewDetails}>
          <VisibilityIcon sx={{ mr: 1 }} fontSize="small" />
          View Details
        </MenuItem>
        <MenuItem onClick={handleChangeRole}>
          <EditIcon sx={{ mr: 1 }} fontSize="small" />
          Change Role
        </MenuItem>
        {selectedUser?.isActive ? (
          <MenuItem onClick={() => handleSuspendActivate('suspend')}>
            <PersonOffIcon sx={{ mr: 1 }} fontSize="small" />
            Suspend User
          </MenuItem>
        ) : (
          <MenuItem onClick={() => handleSuspendActivate('activate')}>
            <PersonAddIcon sx={{ mr: 1 }} fontSize="small" />
            Activate User
          </MenuItem>
        )}
        <MenuItem onClick={handleImpersonate}>
          <VisibilityIcon sx={{ mr: 1 }} fontSize="small" />
          Impersonate
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
          Delete User
        </MenuItem>
      </Menu>

      <Dialog open={addUserDialogOpen} onClose={() => setAddUserDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New User</DialogTitle>
        <form onSubmit={handleSubmit(handleAddUser)}>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Name"
                    fullWidth
                    error={!!errors.name}
                    helperText={errors.name?.message}
                  />
                )}
              />
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Email"
                    type="email"
                    fullWidth
                    error={!!errors.email}
                    helperText={errors.email?.message}
                  />
                )}
              />
              <Controller
                name="role"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.role}>
                    <InputLabel>Role</InputLabel>
                    <Select {...field} label="Role">
                      <MenuItem value="athlete">Athlete</MenuItem>
                      <MenuItem value="trainer">Trainer</MenuItem>
                      <MenuItem value="both">Both</MenuItem>
                      <MenuItem value="superuser">Superuser</MenuItem>
                    </Select>
                    {errors.role && <Typography color="error" variant="caption">{errors.role.message}</Typography>}
                  </FormControl>
                )}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddUserDialogOpen(false)} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={loading}>
              {loading ? <CircularProgress size={24} /> : 'Add User'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
        <DialogTitle>Confirm Action</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to {confirmAction} user <strong>{selectedUser?.name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleConfirmAction} variant="contained" color="error" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UsersManagementPage;